<template>
  <ContentWrap>
    <div class="poster-editor">
      <div
        ref="previewRef"
        class="poster-preview"
        :style="{ width: PREVIEW_WIDTH + 'px', minHeight: PREVIEW_MIN_HEIGHT + 'px' }"
        tabindex="0"
        @keydown="onKeydown"
      >
        <img
          v-if="form.bgImg"
          :src="form.bgImg"
          class="poster-preview__bg"
          alt="背景"
          @error="onBgError"
        />
        <div v-else class="poster-preview__placeholder">请上传背景图</div>
        <div
          v-for="item in form.items"
          :key="item.type"
          class="poster-drag"
          :class="[
            `poster-drag--${item.type}`,
            `poster-drag--${item.size || 'medium'}`,
            item.style || '',
            { 'is-selected': selected === item.type }
          ]"
          :style="dragStyle(item)"
          @mousedown.prevent="onDragStart($event, item)"
          @click.stop="selected = item.type"
        >
          <template v-if="item.type === 'avatar'">
            <div class="poster-drag__avatar" />
          </template>
          <template v-else-if="item.type === 'nickname'">
            <span :style="{ color: item.color || '#000', fontSize: nicknameFontPx(item.size) + 'px' }">
              这里是昵称
            </span>
          </template>
          <template v-else>
            <div class="poster-drag__qr" />
          </template>
        </div>
      </div>

      <div class="poster-form">
        <el-form label-width="100px">
          <el-form-item label="海报名称" required>
            <el-input v-model="form.title" maxlength="64" placeholder="请填写海报名称" />
          </el-form-item>

          <el-form-item label="选择模板" required>
            <div class="template-list">
              <div
                v-for="(temp, index) in DEFAULT_TEMPLATES"
                :key="temp.tempid"
                class="template-list__item"
                @click="applyTemplate(index)"
              >
                <div class="template-list__title">{{ temp.title }}</div>
              </div>
              <div class="template-list__item template-list__item--blank" @click="applyBlank">
                <div class="template-list__title">空白模板</div>
              </div>
            </div>
          </el-form-item>

          <el-form-item label="背景图片" required>
            <UploadImg v-model="form.bgImg" height="125px" width="75px" directory="mall/brokerage-poster" />
            <el-text class="w-full" size="small" type="info">
              建议尺寸宽 640、高 ≥1135，比例大于 1:1.5
            </el-text>
          </el-form-item>

          <el-form-item label="海报元素" required>
            <el-button
              v-for="t in elementTypes"
              :key="t"
              :type="selected === t ? 'primary' : 'default'"
              @click="selected = t"
            >
              {{ elementLabel(t) }}
            </el-button>
            <el-text class="w-full" size="small" type="info">
              点击按钮或左侧元素编辑；可用方向键微调位置
            </el-text>
          </el-form-item>

          <template v-if="selectedItem">
            <el-form-item :label="sizeLabel">
              <el-radio-group v-model="selectedItem.size">
                <el-radio value="big">大</el-radio>
                <el-radio value="medium">中</el-radio>
                <el-radio value="small">小</el-radio>
              </el-radio-group>
            </el-form-item>

            <el-form-item v-if="selected === 'avatar'" label="头像样式">
              <el-radio-group v-model="selectedItem.style">
                <el-radio value="">正方形</el-radio>
                <el-radio value="radius">圆角</el-radio>
                <el-radio value="circle">圆形</el-radio>
              </el-radio-group>
            </el-form-item>

            <el-form-item v-if="selected === 'nickname'" label="昵称颜色">
              <div class="color-list">
                <div
                  v-for="c in COLORS"
                  :key="c"
                  class="color-list__block"
                  :class="{ active: selectedItem.color === c }"
                  :style="{ background: c }"
                  @click="selectedItem.color = c"
                />
              </div>
            </el-form-item>

            <el-form-item label="辅助对齐">
              <el-button size="small" @click="alignItem('left')">居左</el-button>
              <el-button size="small" @click="alignItem('center')">居中</el-button>
              <el-button size="small" @click="alignItem('right')">居右</el-button>
            </el-form-item>
          </template>

          <el-form-item label="排序">
            <el-input-number v-model="form.sort" :min="0" class="!w-160px" />
          </el-form-item>

          <el-form-item>
            <el-button type="primary" :loading="saving" @click="submit">保存海报</el-button>
            <el-button @click="emit('cancel')">返回列表</el-button>
          </el-form-item>
        </el-form>
      </div>
    </div>
  </ContentWrap>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { cloneDeep } from 'lodash-es'
import { UploadImg } from '@/components/UploadFile'
import * as PosterApi from '@/api/mall/trade/brokerage/poster'
import type { BrokeragePosterItem, BrokeragePosterVO } from '@/api/mall/trade/brokerage/poster'
import {
  COLORS,
  DEFAULT_TEMPLATES,
  PREVIEW_MIN_HEIGHT,
  PREVIEW_WIDTH,
  blankItems,
  itemSizePx,
  nicknameFontPx
} from './posterDefaults'

const props = defineProps<{
  id?: number
}>()

const emit = defineEmits<{
  (e: 'success'): void
  (e: 'cancel'): void
}>()

const message = useMessage()
const previewRef = ref<HTMLElement>()
const saving = ref(false)
const selected = ref('avatar')
const form = reactive<BrokeragePosterVO>({
  title: '未命名海报',
  bgImg: '',
  items: blankItems(),
  thumb: '',
  sort: 0
})

const elementTypes = ['avatar', 'nickname', 'qrcode'] as const

const selectedItem = computed(() => form.items.find((i) => i.type === selected.value))

const sizeLabel = computed(() => {
  if (selected.value === 'avatar') return '头像尺寸'
  if (selected.value === 'nickname') return '昵称字体'
  return '小程序码尺寸'
})

function elementLabel(t: string) {
  if (t === 'avatar') return '头像'
  if (t === 'nickname') return '昵称'
  return '小程序码'
}

function dragStyle(item: BrokeragePosterItem) {
  const size = itemSizePx(item.type, item.size)
  const style: Record<string, string> = {
    top: `${item.top}px`,
    left: `${item.left}px`
  }
  if (item.type === 'nickname') {
    style.width = 'auto'
    style.height = '30px'
  } else {
    style.width = `${size}px`
    style.height = `${size}px`
  }
  return style
}

async function applyTemplate(index: number) {
  const temp = DEFAULT_TEMPLATES[index]
  if (!temp) return
  try {
    await message.confirm(`确认使用模板「${temp.title}」的布局吗？确定后将重置当前布局`)
    form.items = cloneDeep(temp.items)
    selected.value = 'avatar'
  } catch {
    /* cancel */
  }
}

async function applyBlank() {
  try {
    await message.confirm('确认使用空白模板吗？确定后将重置当前布局')
    form.items = blankItems()
    selected.value = 'avatar'
  } catch {
    /* cancel */
  }
}

function alignItem(align: 'left' | 'center' | 'right') {
  const item = selectedItem.value
  if (!item) return
  const width = item.type === 'nickname' ? 80 : itemSizePx(item.type, item.size)
  if (align === 'left') item.left = 0
  else if (align === 'right') item.left = PREVIEW_WIDTH - width
  else item.left = Math.round((PREVIEW_WIDTH - width) / 2)
  item.align = align
}

let dragState: { type: string; startX: number; startY: number; originLeft: number; originTop: number } | null =
  null

function onDragStart(e: MouseEvent, item: BrokeragePosterItem) {
  selected.value = item.type
  dragState = {
    type: item.type,
    startX: e.clientX,
    startY: e.clientY,
    originLeft: item.left,
    originTop: item.top
  }
  const onMove = (ev: MouseEvent) => {
    if (!dragState) return
    const target = form.items.find((i) => i.type === dragState!.type)
    if (!target) return
    const size = item.type === 'nickname' ? 80 : itemSizePx(item.type, item.size)
    const height = item.type === 'nickname' ? 30 : size
    let left = dragState.originLeft + (ev.clientX - dragState.startX)
    let top = dragState.originTop + (ev.clientY - dragState.startY)
    left = Math.max(0, Math.min(PREVIEW_WIDTH - size, left))
    top = Math.max(0, top)
    target.left = Math.round(left)
    target.top = Math.round(top)
    target.align = ''
    void height
  }
  const onUp = () => {
    dragState = null
    window.removeEventListener('mousemove', onMove)
    window.removeEventListener('mouseup', onUp)
  }
  window.addEventListener('mousemove', onMove)
  window.addEventListener('mouseup', onUp)
}

function onKeydown(e: KeyboardEvent) {
  const item = selectedItem.value
  if (!item) return
  if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return
  e.preventDefault()
  if (e.key === 'ArrowLeft') item.left = Math.max(0, item.left - 1)
  if (e.key === 'ArrowRight') item.left += 1
  if (e.key === 'ArrowUp') item.top = Math.max(0, item.top - 1)
  if (e.key === 'ArrowDown') item.top += 1
  item.align = ''
}

function onBgError(e: Event) {
  const img = e.target as HTMLImageElement
  img.style.display = 'none'
}

async function load() {
  if (!props.id) return
  const data = await PosterApi.getBrokeragePoster(props.id)
  form.id = data.id
  form.title = data.title
  form.bgImg = data.bgImg
  form.items = data.items?.length ? cloneDeep(data.items) : blankItems()
  form.thumb = data.thumb
  form.sort = data.sort ?? 0
  selected.value = form.items[0]?.type || 'avatar'
}

async function submit() {
  if (!form.title?.trim()) {
    message.warning('请填写海报名称')
    return
  }
  if (!form.bgImg?.trim()) {
    message.warning('请上传背景图片')
    return
  }
  if (!form.items?.length) {
    message.warning('海报元素不能为空')
    return
  }
  saving.value = true
  try {
    const payload: BrokeragePosterVO = {
      id: form.id,
      title: form.title.trim(),
      bgImg: form.bgImg,
      items: form.items,
      thumb: form.thumb || form.bgImg,
      sort: form.sort ?? 0
    }
    if (form.id) {
      await PosterApi.updateBrokeragePoster(payload)
    } else {
      await PosterApi.createBrokeragePoster(payload)
    }
    message.success('保存成功')
    emit('success')
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  load()
  previewRef.value?.focus()
})
</script>

<style scoped lang="scss">
.poster-editor {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  min-height: 640px;
}

.poster-preview {
  position: relative;
  flex-shrink: 0;
  border: 1px solid #ccc;
  background: #f5f5f5;
  overflow: hidden;
  outline: none;

  &__bg {
    display: block;
    width: 100%;
    height: auto;
  }

  &__placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 568px;
    color: #999;
    font-size: 14px;
  }
}

.poster-drag {
  position: absolute;
  cursor: move;
  border: 1px dashed transparent;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;

  &.is-selected {
    border-color: #409eff;
  }

  &__avatar {
    width: 100%;
    height: 100%;
    background: #dcdcdc url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Ccircle cx='32' cy='24' r='12' fill='%23999'/%3E%3Cpath d='M12 54c0-11 9-20 20-20s20 9 20 20' fill='%23999'/%3E%3C/svg%3E")
      center / 70% no-repeat;
  }

  &.radius .poster-drag__avatar {
    border-radius: 8px;
  }

  &.circle .poster-drag__avatar {
    border-radius: 50%;
  }

  &__qr {
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(
        0deg,
        #333 0 2px,
        transparent 2px 6px
      ),
      repeating-linear-gradient(90deg, #333 0 2px, #fff 2px 6px);
    border: 1px solid #ddd;
  }
}

.poster-form {
  flex: 1;
  max-width: 640px;
}

.template-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  &__item {
    width: 100px;
    height: 72px;
    border: 1px solid #e5e5e5;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: #fafafa;

    &:hover {
      border-color: #409eff;
    }

    &--blank {
      border-style: dashed;
    }
  }

  &__title {
    font-size: 13px;
    color: #333;
  }
}

.color-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  &__block {
    width: 22px;
    height: 22px;
    border: 1px solid #ddd;
    border-radius: 2px;
    cursor: pointer;

    &.active {
      outline: 2px solid #409eff;
      outline-offset: 1px;
    }
  }
}
</style>
